import type { InspectionStatusId, InspectionType } from '@areapif/areapif-shared'
import { isTerminalInspectionType } from '@areapif/areapif-shared'

import type { ActiveInspection } from '../models/activeInspection'
import type { InspectionState } from './activeInspection'
import { isQualifiedStatusId } from './activeInspection'

/** What `useTranslate` hands a component: a key, optional interpolations, a string back. */
export type TranslateFn = (key: string, options?: Record<string, unknown>) => string

/**
 * The text of the Operativa table's cells, in one place.
 *
 * The table paints these and the CSV export writes them, and the two have to
 * agree: an operator who filtered the table on a state expects the file to
 * name that state the same way. Deriving both from here makes the agreement a
 * matter of construction rather than of keeping two renderings in step.
 */

/** What a cell shows when it has no value, so "nothing" reads the same in every column. */
export const NO_VALUE = '—'

/**
 * Fecha: the browser's short date, or the raw value when it does not parse.
 *
 * A manual inspection has no date until SOSTRAT first matches it, and shows
 * the em dash until then. Checked before parsing: `new Date(null)` is the epoch,
 * which would print a 1970 date rather than say there is none.
 */
export const formatDay = (day: string | null | undefined): string => {
  if (!day) return NO_VALUE
  const date = new Date(day)
  return Number.isNaN(date.getTime()) ? day : date.toLocaleDateString()
}

/** Servicios: the codes joined by commas, an em dash when there are none. */
export const formatServices = (services: ActiveInspection['services']): string => (services.length > 0 ? services.join(', ') : NO_VALUE)

export interface StateBadgeInput {
  state: InspectionState
  statusId: InspectionStatusId
  type: InspectionType
}

/**
 * Estado: the state label, qualified with the raw SOSTRAT status in
 * parentheses on a PIF row. The four states each cover several rungs of the
 * PIF ladder, and the qualifier is what tells a run of `Planificada` rows
 * apart. Terminal rows are not qualified: their vocabulary is two open
 * statuses the shared domain deliberately does not have consumers read by name.
 */
export const formatStateBadgeLabel = (translate: TranslateFn, { state, statusId, type }: StateBadgeInput): string => {
  const stateLabel = translate(`resources.inspections.enums.state.${state}`)
  const qualified = !isTerminalInspectionType(type) && isQualifiedStatusId(statusId)

  return qualified ? `${stateLabel} (${translate(`resources.inspections.enums.status.${statusId}`)})` : stateLabel
}
